<template>
  <span
    ref="triggerRef"
    class="my-tooltip-trigger"
    @mouseenter="handleEnter"
    @mouseleave="handleLeave"
  >
    <slot></slot>
  </span>

  <teleport to="body">
    <transition name="my-tooltip-fade">
      <div
        v-if="visible && label"
        ref="tooltipRef"
        class="my-tooltip-bubble"
        :class="`my-tooltip--${placement}`"
        :style="bubbleStyle"
      >
        <span class="my-tooltip-text text-xs px-s py-xs">{{ label }}</span>
        <span class="my-tooltip-arrow"></span>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, nextTick, onBeforeUnmount } from "vue";

const props = defineProps({
  label: {
    type: String,
    default: "",
  },
  placement: {
    type: String,
    default: "right",
  },
  offset: {
    type: Number,
    default: 8,
  },
  showDelay: {
    type: Number,
    default: 100,
  },
  hideDelay: {
    type: Number,
    default: 80,
  },
});

const triggerRef = ref(null);
const tooltipRef = ref(null);
const visible = ref(false);
const bubbleStyle = ref({});

let showTimer = null;
let hideTimer = null;

const clearTimers = () => {
  if (showTimer) {
    clearTimeout(showTimer);
    showTimer = null;
  }
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
};

const handleEnter = () => {
  clearTimers();
  showTimer = setTimeout(() => {
    visible.value = true;
    nextTick(updatePosition);
  }, props.showDelay);
};

const handleLeave = () => {
  clearTimers();
  hideTimer = setTimeout(() => {
    visible.value = false;
  }, props.hideDelay);
};

const updatePosition = () => {
  const trigger = triggerRef.value;
  const tooltip = tooltipRef.value;
  if (!trigger || !tooltip) return;

  const triggerRect = trigger.getBoundingClientRect();
  const tooltipRect = tooltip.getBoundingClientRect();

  let left = 0;
  let top = 0;

  switch (props.placement) {
    case "top":
      left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
      top = triggerRect.top - tooltipRect.height - props.offset;
      break;
    case "bottom":
      left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
      top = triggerRect.bottom + props.offset;
      break;
    case "left":
      left = triggerRect.left - tooltipRect.width - props.offset;
      top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
      break;
    case "right":
    default:
      left = triggerRect.right + props.offset;
      top = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
      break;
  }

  const vw = document.documentElement.clientWidth;
  const vh = document.documentElement.clientHeight;

  if (left < 8) left = 8;
  if (left + tooltipRect.width > vw - 8) {
    left = vw - tooltipRect.width - 8;
  }
  if (top < 8) top = 8;
  if (top + tooltipRect.height > vh - 8) {
    top = vh - tooltipRect.height - 8;
  }

  bubbleStyle.value = {
    position: "fixed",
    left: `${left}px`,
    top: `${top}px`,
    zIndex: 9999,
  };
};

onBeforeUnmount(() => {
  clearTimers();
});
</script>

<style scoped>
.my-tooltip-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.my-tooltip-fade-enter-active,
.my-tooltip-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.my-tooltip-fade-enter-from,
.my-tooltip-fade-leave-to {
  opacity: 0;
  transform: translateY(2px);
}

.my-tooltip-bubble {
  background: #1e1e1e;
  color: #fff;
  /* padding: 6px 10px; */
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.4;
  max-width: 220px;
  white-space: normal;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
  pointer-events: none;
  font-family: 'Inter';
}

.my-tooltip-text {
  display: inline-block;
}

.my-tooltip-arrow {
  position: absolute;
  width: 8px;
  height: 8px;
  background: #1e1e1e;
  transform: rotate(45deg);
}

/* RIGHT */
.my-tooltip--right .my-tooltip-arrow {
  left: -4px;
  top: 50%;
  transform: translateY(-50%) rotate(45deg);
}

/* LEFT */
.my-tooltip--left .my-tooltip-arrow {
  right: -4px;
  top: 50%;
  transform: translateY(-50%) rotate(45deg);
}

/* TOP */
.my-tooltip--top .my-tooltip-arrow {
  left: 50%;
  bottom: -4px;
  transform: translateX(-50%) rotate(45deg);
}

/* BOTTOM */
.my-tooltip--bottom .my-tooltip-arrow {
  left: 50%;
  top: -4px;
  transform: translateX(-50%) rotate(45deg);
}
</style>
